import Vue from 'vue'
import VueRouter from 'vue-router'
// https://github.com/declandewet/vue-meta
import VueMeta from 'vue-meta'
// Adds a loading bar at the top during page loads.
import NProgress from 'nprogress/nprogress'
import { BootstrapVue, BootstrapVueIcons } from 'bootstrap-vue'
import { toLower } from 'lodash'
import { createGUID, getCartGUID } from '@utils/shopping-cart'

import cookieService from 'vue-cookies'
import store from '@state/store'
import routes from './routes'

Vue.use(VueRouter)
Vue.use(BootstrapVue)
Vue.use(BootstrapVueIcons)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

// If slugs are different, return true
function requestSiteInfo(previousSite, slug) {
  return previousSite === null || toLower(previousSite.slug) !== toLower(slug)
}

function getSiteState(slug) {
  return new Promise((resolve, reject) => {
    const previousSite = store.getters['sites/getSite']
    // If old slug doesn't match new slug, fetch new site based on new slug
    if (requestSiteInfo(previousSite, slug)) {
      store
        .dispatch('sites/fetchSite', {
          slug,
        })
        .then(() => {})
        .catch((error) => {
          reject(error)
        })
      const unwatch = store.watch(
        () => store.getters['sites/getSite'],
        (value) => {
          unwatch()
          resolve(value)
        }
      )
    } else {
      resolve(previousSite)
    }
  })
}

// Creates new shopping cart if one does not already exist
function initShoppingCart() {
  return new Promise((resolve, reject) => {
    // determine if cart already exists
    if (!getCartGUID()) {
      // create new cart, action handles storage of cart id in cookie, etc
      store
        .dispatch('shoppingCart/createShoppingCart')
        .then(() => {
          // new cart successfully created
          store.dispatch('shoppingCart/fetchShoppingCart')
          resolve()
        })
        .catch((error) => {
          // something went wrong creating a new cart
          console.log('Error creating the cart.')
          reject(error)
        })
    } else {
      // cart already exists
      resolve()
    }
  })
}

/**
 * Guards all protected routes and fetches site if needed:
 * @param {Route} to
 * @param {Route} from
 * @param {Function} next
 *
 * See http://routerjs.org/en/advanced/navigation-guards.html
 * for more details.
 */
async function guardRoute(to, from, next) {
  const slug = to.params.site
  try {
    await getSiteState(slug)
    if (
      document.referrer === 'http://localhost:9292/tools/shop_manager' ||
      document.referrer === 'https://v3.dineoncampus.com/'
    ) {
      cookieService.set('c20', createGUID(), '1h')
    }
    if (store.getters['sites/getSite'].shopOnCampus === false) {
      next({
        path: '404',
        name: 'NotFoundPage',
        replace: true,
      })
    }
    if (
      store.getters['sites/getSite'].shopLaunchReady === false &&
      to.name !== 'PreLaunch' &&
      !cookieService.get('c20')
    ) {
      next({
        path: store.getters['sites/getSite'].slug,
        name: 'PreLaunch',
        replace: false,
        params: {
          site: store.getters['sites/getSite'].slug,
        },
      })
    }
    await initShoppingCart()
    next()
  } catch (error) {
    next({
      path: '404',
      name: 'NotFoundPage',
      replace: true,
    })
  }
}

const router = new VueRouter({
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return {
        x: 0,
        y: 0,
      }
    }
  },
})

async function getToken(to, from, next) {
  try {
    await newToken()
  } catch (error) {
    next({
      path: '404',
      name: 'NotFoundPage',
      replace: true,
    })
  }
}

function newToken() {
  return new Promise((resolve, reject) => {
    const sessionToken = store.getters['auth/sessionAuthenticated']
    if (!sessionToken) {
      store
        .dispatch('auth/createSession', {
          apiKey: 'bd8b54948f71b1416d0e5b261edbed36',
        })
        .then(() => {})
        .catch((error) => {
          reject(error)
        })
      const unwatch = store.watch(
        () => store.getters['auth/sessionAuthenticated'],
        (value) => {
          unwatch()
          // setDefaultAuthHeaders(state)
          // store.dispatch('auth/validate')
          resolve(value)
        }
      )
    } else {
      resolve(sessionToken)
    }
  })
}

// Before each route evaluates...
router.beforeEach((routeTo, routeFrom, next) => {
  // If this isn't an initial page load...
  if (routeFrom.name !== null) {
    // Start the route progress bar.
    NProgress.start()
  }
  getToken(routeTo, routeFrom, next)
  if (routeTo.matched.some((m) => m.meta.needSite)) {
    // Check for a token from a cookie. Returns true/false
    const authenticated = store.getters['auth/sessionAuthenticated']
    if (!authenticated) {
      // If false, watch for a new value in the getter
      const unwatch = store.watch(
        () => store.getters['auth/sessionAuthenticated'],
        (value) => {
          unwatch()
          // Proceed to fetch the site
          guardRoute(routeTo, routeFrom, next)
        }
      )
    } else {
      // Proceed to fetch the site
      guardRoute(routeTo, routeFrom, next)
    }
  } else {
    next()
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Complete the animation of the route progress bar.
  NProgress.done()
})

export default router
